import { useMemo, useState } from "react";
import {
  JALALI_MONTHS_FA,
  gregorianDateToJalaliParts,
  jalaliMonthLength,
  jalaliPartsToGregorianDate,
  nowJalaliParts,
  toFaDigits,
  type JalaliParts,
} from "./jalali";

function range(from: number, to: number): number[] {
  return Array.from({ length: to - from + 1 }, (_, index) => from + index);
}

export function JalaliDateField(props: {
  readonly label: string;
  readonly valueGregorian: string;
  readonly onChangeGregorian: (value: string) => void;
  readonly required?: boolean;
  readonly pastYears?: number;
  readonly futureYears?: number;
}) {
  const today = nowJalaliParts();
  const parts = gregorianDateToJalaliParts(props.valueGregorian) ?? today;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<JalaliParts>(parts);
  const years = useMemo(
    () =>
      range(
        today.jy - (props.pastYears ?? 1),
        today.jy + (props.futureYears ?? 3),
      ),
    [today.jy, props.pastYears, props.futureYears],
  );
  const selectDay = (jd: number) => {
    props.onChangeGregorian(
      jalaliPartsToGregorianDate({ jy: view.jy, jm: view.jm, jd }),
    );
    setOpen(false);
  };
  const moveMonth = (delta: number) => {
    const index = view.jy * 12 + view.jm - 1 + delta;
    setView({
      jy: Math.floor(index / 12),
      jm: (index % 12) + 1,
      jd: 1,
    });
  };
  const minYear = years[0]!;
  const maxYear = years[years.length - 1]!;
  const firstDay = new Date(
    `${jalaliPartsToGregorianDate({ jy: view.jy, jm: view.jm, jd: 1 })}T12:00:00`,
  );
  const firstOffset = (firstDay.getDay() + 1) % 7;
  const monthDays = range(1, jalaliMonthLength(view.jy, view.jm));
  const weekdays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

  return (
    <fieldset className="jalali-field jalali-field--date-only">
      <legend>{props.label}</legend>
      <button
        type="button"
        className="jalali-calendar-trigger"
        aria-expanded={open}
        onClick={() => {
          setView(parts);
          setOpen((value) => !value);
        }}
      >
        <span aria-hidden="true">🗓️</span>
        <strong>
          {toFaDigits(
            `${parts.jy}/${String(parts.jm).padStart(2, "0")}/${String(parts.jd).padStart(2, "0")}`,
          )}
        </strong>
        <small>{open ? "بستن تقویم" : "انتخاب از تقویم شمسی"}</small>
      </button>
      {open ? (
        <div className="jalali-calendar" role="dialog" aria-label={props.label}>
          <div className="jalali-calendar__head">
            <button
              type="button"
              aria-label="ماه قبل"
              disabled={view.jy === minYear && view.jm === 1}
              onClick={() => moveMonth(-1)}
            >
              ‹
            </button>
            <select
              aria-label="ماه شمسی"
              value={view.jm}
              onChange={(event) =>
                setView((current) => ({
                  ...current,
                  jm: Number(event.target.value),
                  jd: 1,
                }))
              }
            >
              {JALALI_MONTHS_FA.map((month, index) => (
                <option key={month} value={index + 1}>
                  {month}
                </option>
              ))}
            </select>
            <select
              aria-label="سال شمسی"
              value={view.jy}
              onChange={(event) =>
                setView((current) => ({
                  ...current,
                  jy: Number(event.target.value),
                  jd: 1,
                }))
              }
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {toFaDigits(year)}
                </option>
              ))}
            </select>
            <button
              type="button"
              aria-label="ماه بعد"
              disabled={view.jy === maxYear && view.jm === 12}
              onClick={() => moveMonth(1)}
            >
              ›
            </button>
          </div>
          <div className="jalali-calendar__weekdays" aria-hidden="true">
            {weekdays.map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="jalali-calendar__days">
            {Array.from({ length: firstOffset }, (_, index) => (
              <span key={`empty-${index}`} />
            ))}
            {monthDays.map((day) => {
              const selected =
                parts.jy === view.jy &&
                parts.jm === view.jm &&
                parts.jd === day;
              const isToday =
                today.jy === view.jy &&
                today.jm === view.jm &&
                today.jd === day;
              return (
                <button
                  type="button"
                  key={day}
                  className={`${selected ? "is-selected" : ""}${isToday ? " is-today" : ""}`}
                  aria-pressed={selected}
                  onClick={() => selectDay(day)}
                >
                  {toFaDigits(day)}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {props.required ? (
        <input type="hidden" required value={props.valueGregorian} />
      ) : null}
    </fieldset>
  );
}
